import { GridBaseDataProvider } from '../../../grid/GridBaseDataProvider';
import { Column } from '@nirvana/shared/dtos/types/shared/filters';
import {
  GetRowsRequest,
  GetSummaryRequest,
  GetSummaryResponse,
  SetFiltersRequest,
  UpdateSettingsRequest,
  UpdateSettingsResponse,
} from '../../../grid/types/common';
import { ColumnKeys } from '../types';
import { controlComplianceScoreMap } from '../../common/util';
import { ConsoleRequest, DataProviderType } from 'apps/webcomponents/src/utils/request';
import { handleExactMatchCustomValues } from '../../../grid/utils/filters';
import { ComplianceEvents } from '../../common/types';
import { NirvanaErrorFeatureUnavailable } from '@nirvana/shared/error';
import { NirvanaErrorCode } from '@nirvana/shared/dtos/types/shared/communication';
import { ComplianceErrorEmptyState } from '../../common/error';

export class ComplianceAppGridDataProvider extends GridBaseDataProvider<Column, ColumnKeys> {
  response;
  companyConsoleResponse;
  isEmptyRowsWithOverallChecks: boolean;
  dependencies;
  dispatchFn;

  constructor(dependencies, dispatchFn) {
    super();
    this.dependencies = dependencies;
    this.dispatchFn = dispatchFn;
  }

  async getSummary(request: GetSummaryRequest<ColumnKeys>): Promise<GetSummaryResponse> {
    // This is an adapter between grid filters changes to a format compatible with
    // compliance reports microservice getOverviewData.

    const complianceRequest = {
      companyId: Application.Main.getUserCompany()?.id ?? null,
      complianceStandard: null,
      filters: {
        passedPercentage: [],
      } as Record<string, any>,
      pagination: {
        maxItems: request.pageSize,
        pageNumber: (request.pageNumber as number) <= 0 ? 1 : request.pageNumber,
      },
    };

    request.filters.forEach(filter => {
      const filterEntry = Object.entries(filter);

      if (filterEntry.length > 0) {
        const [filterKey, filterValue] = filterEntry[0];
        handleExactMatchCustomValues(filterValue);

        if (filterKey === ColumnKeys.COMPLIANCE_STANDARD) {
          if (filterValue?.value) {
            complianceRequest.complianceStandard = filterValue.value;
          }
        } else if (filterKey === ColumnKeys.CONTROL_COMPLIANCE_SCORE) {
          if (filterValue?.in) {
            filterValue.in.forEach((x: string) => {
              if (x === 'NA') {
                complianceRequest.filters.includeNotEvaluated = true;
              } else if (controlComplianceScoreMap[x]) {
                complianceRequest.filters.passedPercentage.push(controlComplianceScoreMap[x].queryFilter);
              }
            });
          }
        } else if (filterKey === ColumnKeys.COMPANY) {
          if (filterValue?.exactMatch) {
            complianceRequest.companyId = filterValue.exactMatch;
          }
        }
      }
    });

    if (complianceRequest.complianceStandard) {
      if (!this.response) {
        await Promise.allSettled([
          this.dependencies.api.clients.ComplianceReports.getOverviewData(complianceRequest).toPromise(),
          ConsoleRequest.createRequest<any>({
            dataProviderType: DataProviderType.CORE,
            endpoint: 'Company',
            method: 'getCompanyById',
            payload: {
              companyId: complianceRequest.companyId,
            },
          }),
        ]).then(([compliancePromiseResult, companyPromiseResult]) => {
          if (compliancePromiseResult.status === 'rejected') {
            if (compliancePromiseResult.reason?.data?.code === NirvanaErrorCode.FEATURE_CURRENTLY_NOT_AVAILABLE) {
              throw new NirvanaErrorFeatureUnavailable();
            }
            throw new Error('Error calling ComplianceReports client request');
          }

          if (companyPromiseResult.status === 'rejected') {
            throw new Error('Error calling getCompanyById console request');
          }

          if (compliancePromiseResult.value.checksStats.overAllStats.totalChecks === 0) {
            throw new ComplianceErrorEmptyState('No findings found');
          }

          this.response = compliancePromiseResult.value;
          this.companyConsoleResponse = companyPromiseResult.value;
          this.isEmptyRowsWithOverallChecks =
            this.response.checksStats.overAllStats.totalChecks !== 0 && this.response.pagination.totalItems === 0;

          this.dispatchFn(ComplianceEvents.COMPLIANCE_GRID_DATA_LOADED, {
            filters: request.filters,
            data: {
              ...compliancePromiseResult.value,
              features: {
                iorUsable: this.companyConsoleResponse?.data?.specifics?.features?.iorUsable ?? false,
                compliancePremiumUsable:
                this.companyConsoleResponse?.data?.specifics?.features?.compliancePremiumUsable ?? false,
              }
            },
          });
        });
      }
    }

    const summary: GetSummaryResponse = {
      summary: `${this.response?.pagination?.totalItems} items`,
      numItems: this.response?.pagination?.totalItems,
    };

    summary.extraDetails = {
      company: {
        id: this.companyConsoleResponse?.data?._id ?? '',
        name: this.companyConsoleResponse?.data?.name ?? '',
        iorUsable: this.companyConsoleResponse?.data?.specifics?.features?.iorUsable ?? false,
        compliancePremiumUsable:
          this.companyConsoleResponse?.data?.specifics?.features?.compliancePremiumUsable ?? false,
        totalEndpoints: this.response?.totalEndpoints ?? 0,
      },
      disableIorUserMonitoring: this.companyConsoleResponse?.data?.specifics?.disableIorUserMonitoring ?? false,
    };

    return summary;
  }

  async getRows(request: GetRowsRequest<Column, ColumnKeys>): Promise<any> {
    if (!this.response) {
      return { rawEvents: [] };
    }

    // this.response should be already fetched from getSummary above.
    const response = this.response;
    const totalChecks = this.response?.checksStats.overAllStats.totalChecks ?? 0;

    this.response = null;
    return { rawEvents: totalChecks !== 0 ? response.controlsOverview ?? [] : [] };
  }

  async updateSettings(params: UpdateSettingsRequest<Column>): Promise<UpdateSettingsResponse> {
    return {};
  }

  async setFilters(request: SetFiltersRequest<ColumnKeys>): Promise<{} | undefined> {
    return {};
  }
}
